import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, X, Flame } from "lucide-react";

const DISMISS_KEY = "stb_reminder_dismissed_v1";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function ReminderPrompt() {
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed === todayIso()) return;
    Promise.all([
      api.get("/notifications/reminder-status").catch(() => null),
      api.get("/stats/me").catch(() => null),
    ]).then(([status, stats]) => {
      if (status?.data?.should_prompt) {
        setShouldPrompt(true);
        setStreak(stats?.data?.streak_days || 0);
      }
    });
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, todayIso());
    setShouldPrompt(false);
  };

  if (!shouldPrompt) return null;

  return (
    <div className="border border-brand-emerald/40 bg-brand-emerald/10 rounded-2xl p-5 mb-8 flex items-center gap-4" data-testid="reminder-prompt">
      <div className="w-12 h-12 border border-brand-emerald/40 bg-background/60 rounded-xl flex items-center justify-center shrink-0">
        {streak > 0 ? (
          <Flame className="w-5 h-5 text-brand-crimson" />
        ) : (
          <Bell className="w-5 h-5 text-brand-emerald" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-emerald mb-1">Daily reminder</div>
        <div className="font-display font-black text-lg tracking-tight">
          {streak >= 3
            ? `Don't break your ${streak}-day streak!`
            : streak >= 1
              ? `You're on a ${streak}-day streak — keep it going.`
              : "Time for today's practice."}
        </div>
        <div className="text-xs font-mono text-muted-foreground mt-1">A 5-minute session is all it takes.</div>
      </div>
      <Link to="/dictation">
        <Button className="rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-background text-xs uppercase tracking-[0.2em] font-bold px-6 h-11" data-testid="reminder-start-btn">
          Practice now
        </Button>
      </Link>
      <button
        onClick={dismiss}
        aria-label="Dismiss reminder"
        className="w-9 h-9 border border-border rounded-lg hover:bg-secondary flex items-center justify-center shrink-0"
        data-testid="reminder-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
